import { getDateRangeDescr, groupBy, isArray, numericTextSort, str } from "../omnia"
import { HoldingNotification, HoldingNotificationId, IDashboardService, IFieldsService } from "../types"
import { msg } from "./strings"
import { NotificationEntry, NotificationGroup } from "./types"

import NtfId = HoldingNotificationId
type Entry = NotificationEntry

interface GroupTemplate {
    summary: string
    description?: string
    getDetail?(ntf: HoldingNotification, svc: IFieldsService): Entry
}

export function formatRemoteNotifications(ntf: HoldingNotification[], svc: IDashboardService): NotificationGroup[] {

    const groups = groupBy(ntf, n => n.NotificationId).sort((a, b) => a.key - b.key)

    return groups.map(g => formatGroup(g.key, g.values, svc))

}

function formatGroup(id: HoldingNotificationId, ntf: HoldingNotification[], svc: IDashboardService): NotificationGroup {

    const tml = messagesIndex[id] || {
        summary: NtfId[id],
        description: NtfId[id]
    }

    return {
        id: "generic-" + id,
        summary: tml.summary,
        description: tml.description,
        details: numericTextSort(getDetails(ntf, svc.getFieldService(), tml), t => t.text)
    } as NotificationGroup

}

function getDetails(ntf: HoldingNotification[], svc: IFieldsService, tmpl: GroupTemplate): { text: string }[] {

    const gd = tmpl.getDetail || (n => {

        const text = n.CroppingId
            ? getCroppingDescr(n.CroppingId, svc)
            : getFieldDescr(n.FieldId, svc)

        return {
            text
        } as Entry

    })

    return ntf
        .map(ntf => ({
            ntf,
            ...gd(ntf, svc)
        }))
        .filter(t => t && t.text)
        .map(e => ({
            ...e,
            location: e.location || getLocation(e.ntf)
        }))

}

function getCroppingDescr(id: number, svc: IFieldsService) {
    const c = svc.findCropping(id)
    return c ? `${c.description} - ${getDateRangeDescr(c.startDate, c.endDate)}` : `Cropping #${id}`
}

function getFieldDescr(id: number, svc: IFieldsService) {
    const field = svc.findField(id)
    return field ? field.description : `${str.Field} #${id}`
}

const messagesIndex: { [key: number]: GroupTemplate } = {

    [NtfId.Cropping_Area_LargerThanField_1]: {

        ...msg.cropsLargerThanField,

        getDetail: (n, svc) => {

            const c = svc.findCropping(n.CroppingId)

            const text = c
                ? `${c.description} - field area ${c.fieldVm.area} ha`
                : `${str.Cropping} #${n.CroppingId}`

            return { text }

        }
    },

    [NtfId.Cropping_Area_LargerThanField_2]: {

        ...msg.cropsLargerThanField2,

        getDetail: (n, svc) => {

            const f = svc.findField(n.FieldId)

            const { cropping, startDate, endDate, groupArea } = <any>(n.Data || {})

            const text = f && isArray(cropping)
                ? `${f.description} - ${cropping.length} crops - ${getDateRangeDescr(startDate, endDate)} - total area ${groupArea} ha`
                : `${str.Cropping} #${n.CroppingId}`

            return { text }

        }

    },

    [NtfId.Cropping_Boundary_OutsideFieldBoundary]: msg.cropOutsideFieldBoundary,

    [NtfId.Cropping_Boundary_Overlaps]: msg.cropBoundaryOverlaps,

    [NtfId.Field_Boundary_Overlaps]: msg.fieldBoundaryOverlaps,

    [NtfId.Cropping_Dates_WrongYear]: {

        ...msg.wrongYear,

        getDetail: (n, svc) => {

            const c = svc.findCropping(n.CroppingId)

            const text = c
                ? `${c.description} - ${getDateRangeDescr(c.startDate, c.endDate)} / cropping year ${c.yearId}`
                : `${str.Cropping} #${n.CroppingId}`

            return { text }

        }

    },

    [NtfId.Cropping_Dates_StartBeforeEnd]: msg.startBeforeEnd,

    [NtfId.Cropping_History_PrevNotSet]: msg.historyPrevNotSet

}

function getLocation(n: HoldingNotification) {
    return `/fields/edit-${n.CroppingId ? 'cropping' : 'field'}/${n.CroppingId || n.FieldId}`
}
